/**
 * Keeps a backup of the essay answer and offers a restore when the CKEditor essay unexpectedly
 * goes blank. Editor agnostic: an editor registers an adapter (see registerEditor).
 */

import Alert from 'react-s-alert';

import alertService from '../../../AlertService';
import alertLevel from '../../../../components/alert/AlertLevel';
import { reportError } from '../../../ErrorReporting';
import { t } from '../../../i18nService';
import marketplacePropertiesService from '../../../MarketplacePropertiesService';
import sessionReader from '../../../../store/readHelpers/SessionReader';
import { responseSendStatus } from '../../../ResponseSendingQueue';
import {
    essayRestoreAlertTypes,
    RESTORE_SAVE_ATTEMPTS,
    RESTORE_SAVE_RETRY_MS,
} from '../../../../constants/EssayRestoreConstants';

export function isBlankContent(text) {
    return typeof text !== 'string' || text.trim().length === 0;
}

/** Per interaction, as a question can hold more than one essay. */
export function essayRestoreKey(contentRevisionId, interactionId) {
    return `${contentRevisionId}:${interactionId}`;
}

/** Module level, so a backup survives navigating away and back. */
const registry = new Map();

let activeAlertId;

let registryAssessmentRunId;

/** Separate flag: undefined is a valid run id (standalone preview). */
let registryRunKnown = false;

function getEntry(key) {
    return registry.get(key);
}

/** Reporting must never stop the card from showing. */
function reportQuietly(details) {
    try {
        reportError(details);
    } catch (e) {
        // ignore
    }
}

const essayRestoreService = {

    /** MPP ENABLE_UNDO_ESSAY_BLANK. Off: no entry is ever created, so nothing runs. */
    isEnabled() {
        return marketplacePropertiesService.isEssayRestoreEnabled();
    },

    /**
     * @param {string} key see essayRestoreKey
     * @param {object} adapter
     * @param {function(): string} adapter.getContent
     * @param {function(string): void} adapter.setContent
     * @param {function(function(): boolean): *} adapter.saveContent - receives an isCurrent() check
     */
    registerEditor(key, adapter) {
        if (!this.isEnabled() || !key || !adapter) {
            return;
        }

        this.discardRegistryFromAnotherRun();

        const existing = getEntry(key);
        if (existing) {
            existing.adapter = adapter;
            // Any restore in flight belonged to the previous adapter.
            existing.isRestoring = false;
            existing.restoreGeneration += 1;
            existing.closedByRestore = false;
            // promptDismissed is kept: a remount (e.g. contrast change) is not a new blanking.
            existing.pinnedSeed = undefined;
            if (existing.backup) {
                // The remount is seeded from the store, which can be older than the backup.
                existing.pinnedSeed = this.readContent(existing, key);
                return;
            }
        } else {
            registry.set(key, {
                adapter,
                backup: '',
                suppressUntil: 0,
                alertId: undefined,
                isRestoring: false,
                restoreGeneration: 0,
                promptDismissed: false,
                closedByRestore: false,
                pinnedSeed: undefined,
                successAlertId: undefined,
            });
        }

        this.captureContent(key);
    },

    /** The backup is kept so it survives navigation. */
    unregisterEditor(key) {
        const entry = getEntry(key);
        if (!entry) {
            return;
        }
        this.closeAlert(key);
        this.closeSuccessAlert(entry);
        entry.adapter = undefined;
        // A pending write can never settle now; release it.
        entry.isRestoring = false;
        entry.restoreGeneration += 1;
    },

    /** A deliberate clear: nothing to offer back. */
    discardBackup(key) {
        const entry = getEntry(key);
        if (!entry) {
            return;
        }
        this.closeAlert(key);
        // A restore still saving must not land on a question the candidate has just reset.
        entry.isRestoring = false;
        entry.restoreGeneration += 1;
        entry.backup = '';
        entry.pinnedSeed = undefined;
    },

    /** Marks the next change as programmatic. */
    suppressDetection(key, durationMs = 1000) {
        const entry = getEntry(key);
        if (entry) {
            entry.suppressUntil = Date.now() + durationMs;
        }
    },

    captureContent(key) {
        const entry = getEntry(key);
        if (!entry?.adapter || entry.pinnedSeed !== undefined) {
            return;
        }

        const content = this.readContent(entry, key);
        if (!isBlankContent(content)) {
            entry.backup = content;
        }
    },

    /** True while the content is still the remount's seed or a programmatic write. */
    keepsPinnedBackup(entry, content) {
        if (entry.pinnedSeed === undefined) {
            return false;
        }
        if (content === entry.pinnedSeed) {
            return true;
        }
        if (Date.now() < entry.suppressUntil) {
            entry.pinnedSeed = content;
            return true;
        }
        entry.pinnedSeed = undefined;
        return false;
    },

    getBackupContent(key) {
        return getEntry(key)?.backup || '';
    },

    hasRestorableBackup(key) {
        return !isBlankContent(this.getBackupContent(key));
    },

    isRestoreInProgress(key) {
        return Boolean(getEntry(key)?.isRestoring);
    },

    readContent(entry, key) {
        try {
            return entry.adapter.getContent();
        } catch (e) {
            reportQuietly({ message: 'EssayRestoreService: failed reading essay content', id: key });
            return undefined;
        }
    },

    /**
     * Refreshes the backup on non-blank text, and prompts when the text goes blank.
     *
     * @returns {boolean} true when a restore prompt was raised
     */
    handleContentChange(key) {
        const entry = getEntry(key);
        if (!this.isEnabled() || !entry?.adapter) {
            return false;
        }

        const content = this.readContent(entry, key);
        if (content === undefined) {
            return false;
        }

        if (!isBlankContent(content)) {
            // The answer is back, so a later blanking prompts again.
            entry.promptDismissed = false;
            if (!this.keepsPinnedBackup(entry, content)) {
                entry.backup = content;
            }
            return false;
        }

        // Only prompting is suppressed; the backup above still updates.
        if (Date.now() < entry.suppressUntil) {
            return false;
        }

        if (!entry.backup) {
            return false;
        }

        if (entry.alertId) {
            return false;
        }

        if (entry.promptDismissed) {
            return false;
        }

        this.reportUnexpectedBlanking(key, entry);
        this.showRestoreAlert(key);
        return true;
    },

    reportUnexpectedBlanking(key, entry) {
        const details = {
            key,
            previousContentLength: entry.backup.length,
            detectedAt: new Date().toISOString(),
        };

        console.log(`Essay content blanked unexpectedly, ${key}.`, details);
        reportQuietly({ message: 'Essay content blanked unexpectedly', id: key, details });
    },

    /** One card at a time. The replaced card is not counted as dismissed. */
    dismissActiveAlert() {
        if (!activeAlertId) {
            return;
        }

        registry.forEach((entry) => {
            if (entry.alertId === activeAlertId) {
                entry.alertId = undefined;
            }
            if (entry.successAlertId === activeAlertId) {
                entry.successAlertId = undefined;
            }
        });

        Alert.close(activeAlertId);
        activeAlertId = undefined;
    },

    showRestoreAlert(key) {
        const entry = getEntry(key);
        if (!entry) {
            return;
        }
        this.dismissActiveAlert();

        // No `position`: App.scss centres these alerts. onClose is scoped to this card's id.
        const thisAlertId = alertService.showAlert(t('contentRestore.alert.message'), {
            html: true,
            timeout: 'none',
            customFields: {
                type: essayRestoreAlertTypes.BLANKED,
                level: alertLevel.WARNING,
                onRestore: () => this.restoreContent(key),
                onCancel: () => this.dismissPrompt(key, thisAlertId),
            },
            onClose: () => {
                const currentEntry = getEntry(key);
                if (currentEntry?.alertId === thisAlertId) {
                    currentEntry.alertId = undefined;
                    currentEntry.promptDismissed = !currentEntry.closedByRestore;
                    currentEntry.closedByRestore = false;
                }
                if (activeAlertId === thisAlertId) {
                    activeAlertId = undefined;
                }
            },
        });
        entry.alertId = thisAlertId;
        activeAlertId = thisAlertId;
    },

    /**
     * Cancel, recorded at the click: onClose can arrive after the player has closed the card
     * itself (e.g. a contrast change), when its id guard no longer passes.
     *
     * @param {string} key
     * @param {string} alertId the card the candidate cancelled
     */
    dismissPrompt(key, alertId) {
        const entry = getEntry(key);
        // A newer card is a newer blanking.
        if (!entry || (entry.alertId && entry.alertId !== alertId)) {
            return;
        }
        entry.promptDismissed = true;
        this.flushDismissedBlank(key, entry);
    },

    /** Saves the blank now, so a remount does not read the old answer back from the store. */
    flushDismissedBlank(key, entry) {
        if (!entry.adapter) {
            return;
        }
        const generation = entry.restoreGeneration;
        const isCurrent = () => getEntry(key) === entry && entry.restoreGeneration === generation;

        Promise.resolve()
            .then(() => (isCurrent() ? entry.adapter.saveContent(isCurrent) : undefined))
            .catch(() => {
                reportQuietly({ message: 'EssayRestoreService: failed saving a dismissed blanking', id: key });
            });
    },

    showRestoreSuccessAlert(key) {
        this.dismissActiveAlert();

        const thisAlertId = alertService.showAlert(t('contentRestore.success.message'), {
            html: true,
            timeout: 'none',
            customFields: {
                type: essayRestoreAlertTypes.RESTORED,
                level: alertLevel.SUCCESS,
            },
            onClose: () => {
                const currentEntry = getEntry(key);
                if (currentEntry?.successAlertId === thisAlertId) {
                    currentEntry.successAlertId = undefined;
                }
                if (activeAlertId === thisAlertId) {
                    activeAlertId = undefined;
                }
            },
        });
        const entry = getEntry(key);
        if (entry) {
            entry.successAlertId = thisAlertId;
        }
        activeAlertId = thisAlertId;
    },

    closeSuccessAlert(entry) {
        if (!entry.successAlertId) {
            return;
        }
        if (activeAlertId === entry.successAlertId) {
            activeAlertId = undefined;
        }
        Alert.close(entry.successAlertId);
        entry.successAlertId = undefined;
    },

    /** The player closing the card; not a dismissal. */
    closeAlert(key) {
        const entry = getEntry(key);
        if (entry?.alertId) {
            if (activeAlertId === entry.alertId) {
                activeAlertId = undefined;
            }
            Alert.close(entry.alertId);
            entry.alertId = undefined;
        }
    },

    /**
     * Writes the backup back and saves it. Success only once the save is PERSISTED.
     *
     * @returns {Promise<boolean>}
     */
    restoreContent(key) {
        const entry = getEntry(key);
        if (!this.isEnabled() || !entry || !entry.adapter || entry.isRestoring || !entry.backup) {
            return Promise.resolve(false);
        }

        const adapter = entry.adapter;

        entry.isRestoring = true;
        const generation = entry.restoreGeneration;
        const isCurrent = () => getEntry(key) === entry && entry.restoreGeneration === generation;

        this.suppressDetection(key);

        try {
            adapter.setContent(entry.backup);
        } catch (e) {
            entry.isRestoring = false;
            reportQuietly({ message: 'EssayRestoreService: failed restoring essay content', id: key });
            return Promise.resolve(false);
        }

        return Promise.resolve()
            .then(() => {
                if (!isCurrent()) {
                    return false;
                }
                return this.saveUntilSent(adapter, isCurrent, RESTORE_SAVE_ATTEMPTS);
            })
            .then(status => status === responseSendStatus.PERSISTED)
            .catch(() => {
                reportQuietly({ message: 'EssayRestoreService: failed saving restored content', id: key });
                return false;
            })
            .then((persisted) => {
                // The editor was torn down or re-registered meanwhile.
                if (!isCurrent()) {
                    return false;
                }
                entry.isRestoring = false;

                if (!persisted) {
                    alertService.showEssayEditorUnableToSaveAlert();
                    return false;
                }

                entry.closedByRestore = true;
                this.closeAlert(key);
                this.showRestoreSuccessAlert(key);
                console.log(`Essay content restored, ${key}.`, { restoredAt: new Date().toISOString() });
                return true;
            });
    },

    /** QUEUED means another request was in flight; try again once the queue has moved on. */
    saveUntilSent(adapter, isCurrent, attemptsLeft) {
        return Promise.resolve(adapter.saveContent(isCurrent)).then((status) => {
            if (status !== responseSendStatus.QUEUED || attemptsLeft <= 1 || !isCurrent()) {
                return status;
            }
            return new Promise(resolve => setTimeout(resolve, this.saveRetryDelayMs))
                .then(() => (isCurrent() ? this.saveUntilSent(adapter, isCurrent, attemptsLeft - 1) : status));
        });
    },

    saveRetryDelayMs: RESTORE_SAVE_RETRY_MS,

    /**
     * A new run starts empty, so another attempt's answer is never restored. Uses the parent run,
     * as composite tests switch child runs without a reload.
     */
    discardRegistryFromAnotherRun() {
        let assessmentRunId;
        try {
            assessmentRunId = sessionReader.getAssessmentRunId(true);
        } catch (e) {
            return;
        }

        if (registryRunKnown && registryAssessmentRunId !== assessmentRunId) {
            this.reset();
        }
        registryRunKnown = true;
        registryAssessmentRunId = assessmentRunId;
    },

    reset() {
        this.dismissActiveAlert();
        registry.clear();
        registryAssessmentRunId = undefined;
        registryRunKnown = false;
    },
};

export default essayRestoreService;
